import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLogin } from "../../auth/useLogin";
import { startGoogleSignIn } from "../../auth/googleSignIn";
import Logo from "../../components/ui/Logo";
import { track, startSessionReplay, stopSessionReplay } from "../../lib/analytics";
import { getInboxProvider } from "../../lib/email-providers";
import { detectInAppBrowser } from "../../lib/in-app-browser";

export default function LoginPage() {
  const login = useLogin();
  const location = useLocation();
  // Route-driven mode. /signup + /register present as "Create your
  // account" so cold-ad teachers don't bounce thinking they need an
  // existing account; /login keeps "Sign in" framing for returning
  // users coming from the nav. The underlying auth (Stytch magic link
  // + Google) is identical — only the copy changes.
  const isSignup =
    location.pathname === "/signup" || location.pathname === "/register";
  const [email, setEmail] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState("");
  // login.isPending tracks the in-flight magic-link request; no manual
  // isLoading useState needed.
  const isLoading = login.isPending;

  // Session replay on auth pages. H7's 80% Google OAuth dropoff is
  // the dominant funnel leak; recordings here let us see what
  // teachers do BEFORE the redirect to Google (do they hover the
  // button, click and reconsider, etc.). Cheap to enable here
  // because email inputs are masked at the SDK config layer
  // (lib/analytics.ts session_recording.maskInputOptions.email).
  useEffect(() => {
    startSessionReplay();
    return () => stopSessionReplay();
  }, []);

  // Honor a `?returnTo=/some/path` deep link by stashing it for post-auth
  // redirection. Internal only. Guard against open redirects.
  const returnTo = (() => {
    try {
      const raw = new URLSearchParams(location.search).get("returnTo");
      if (!raw) return null;
      if (!raw.startsWith("/")) return null;
      if (raw.startsWith("//")) return null;
      return raw;
    } catch {
      return null;
    }
  })();

  // Translate `?error=...` codes coming back from the Google OAuth
  // callback (E25 redirects on failure) AND the magic-link callback
  // (E37 redirects on failure) into a teacher-readable banner shown
  // above the form. Falls through to no-error if the code isn't one
  // we recognize.
  const oauthErrorBanner = (() => {
    try {
      const code = new URLSearchParams(location.search).get("error");
      if (!code) return null;
      switch (code) {
        case "google_auth_invalid":
        case "google_auth_failed":
          return "We couldn't complete that sign-in. Please try again, or use email if Google keeps failing.";
        // E37: magic-link callback error codes. The backend forwards
        // Stytch's `error_type` directly so we map the common ones to
        // actionable copy. Unknown codes fall through to a generic
        // message rather than confusing teachers with Stytch jargon.
        case "magic_link_invalid":
          return "That sign-in link looks broken. Send yourself a fresh one.";
        case "unable_to_auth_magic_link":
          return "This sign-in link is already used or expired. Send a fresh one and use it within 30 minutes.";
        case "stale_factors":
          return "This sign-in link expired before you could use it. Send a fresh one.";
        case "magic_link_failed":
          return "We couldn't sign you in with that link. Try sending a fresh one.";
        default:
          return null;
      }
    } catch {
      return null;
    }
  })();
  // Sync the displayed error to the URL's `?error=` param on every
  // route change. If the URL carries an OAuth error code, display
  // it; if the user clicks the /login ↔ /signup cross-link (same
  // component, no unmount), the param disappears and we clear.
  // Subsequent retries clear it via setError("") in the submit
  // handlers.
  useEffect(() => {
    setError(oauthErrorBanner ?? "");
    // Tie to pathname + search so cross-link navigation re-evaluates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const stashReturnTo = () => {
    try {
      if (returnTo) window.localStorage.setItem("td_return_to", returnTo);
      else window.localStorage.removeItem("td_return_to");
    } catch {
      // ignore. localStorage can be disabled.
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    // Defensive normalization: trim + lowercase before handing to Stytch.
    // Teachers paste from contacts apps / autofill that occasionally
    // includes trailing spaces, and Stytch is case-sensitive in a few
    // edge paths. Normalizing here means "isaiahballah@gmail.com" and
    // " ISAIAHBALLAH@gmail.com  " resolve to the same Stytch user.
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Please enter your email.");
      return;
    }
    try {
      stashReturnTo();
      await login.mutateAsync(normalizedEmail);
      // Funnel event — fires AFTER Stytch accepts the request so we're
      // measuring "magic link delivered" not "form-submit-button clicked
      // even if it errored". Sandwich event between this and
      // free_credits_count_assigned reveals the magic-link-not-clicked
      // bucket directly, closing the data gap #1 we'd been inferring
      // from page-view ratios (see docs/funnel.md). No email captured
      // here; identify happens later when the link is clicked.
      track("signup_email_submitted", {
        mode: isSignup ? "signup" : "signin",
        returnTo: returnTo ?? null,
      });
      // Reset the input state to the normalized form so the SentState
      // "we sent a link to {email}" line shows what Stytch actually
      // received (not the raw "  ISAIAH@GMAIL.com  " they typed).
      setEmail(normalizedEmail);
      setIsSent(true);
    } catch (err) {
      // Also track failures so we can see Stytch errors / invalid
      // addresses / rate limits as a distinct cohort from teachers
      // who simply never click the button. errorCode + stytchStatus
      // come from the backend (handleLoginOrSignup) which now surfaces
      // Stytch's error_type — without this, the 2026-05-24 URL allowlist
      // regression only showed as "Failed to send magic link" with no
      // signal to distinguish from user typos or rate limits.
      const details =
        err && typeof err === "object" && "details" in err
          ? ((err as { details?: Record<string, unknown> }).details ?? {})
          : {};
      const httpStatus =
        err && typeof err === "object" && "status" in err
          ? (err as { status?: number }).status
          : undefined;
      track("signup_email_submit_failed", {
        mode: isSignup ? "signup" : "signin",
        message: err instanceof Error ? err.message : String(err),
        errorCode: details.errorCode ?? null,
        stytchStatus: details.stytchStatus ?? null,
        httpStatus: httpStatus ?? null,
      });
      // E36: targeted error copy. Previously every failure mode collapsed
      // to "Failed to send magic link" — teachers couldn't tell whether
      // to fix their input, wait, or contact support. Now we read
      // errorCode (from E31 backend propagation) and show actionable
      // copy that maps to what the teacher should do next.
      setError(toFriendlyAuthError(details.errorCode, isSignup));
    }
  };

  const handleGoogleClick = () => {
    stashReturnTo();
    // Capture Google OAuth attempts on the same funnel surface as
    // email-submitted. A teacher who clicks Google but bounces from
    // the OAuth popup is a different drop-off bucket than a teacher
    // who never enters their email.
    track("signup_google_clicked", {
      mode: isSignup ? "signup" : "signin",
      returnTo: returnTo ?? null,
    });
    startGoogleSignIn().catch((err) => {
      setError(err instanceof Error ? err.message : "Failed to start Google sign-in");
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50/60 via-white to-stone-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Wordmark */}
        <div className="flex justify-center mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-3 group"
            aria-label="TeacherDashboard home"
          >
            <Logo className="w-10 h-10 transition-transform group-hover:scale-105" />
            <span className="text-2xl font-bold tracking-tight text-stone-900">
              TeacherDashboard
            </span>
          </Link>
        </div>

        {/* E41 v2: in-app browser hint + hard defense. FB / Instagram /
            TikTok / X in-app browsers routinely break Google OAuth
            (popup blocked, cookies stripped) and sometimes break
            magic-link clicks (the link opens a new context without
            the auth cookie). Real prod evidence today: 21:14 UTC
            Instagram-IAB user clicked Google sign-in IN SPITE of
            seeing the banner — silently lost in the IAB. Now we
            (a) show the banner and (b) hide the Google button entirely
            in IAB, routing them to email magic-link which actually
            works (Gmail / Mail tap opens the link in real browser). */}
        <InAppBrowserHint />

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] ring-1 ring-stone-200/80 p-8">
          {isSent ? (
            <SentState email={email} onReset={() => { setIsSent(false); setEmail(""); }} />
          ) : (
            <SignInForm
              mode={isSignup ? "signup" : "signin"}
              email={email}
              setEmail={setEmail}
              isLoading={isLoading}
              error={error}
              onGoogle={handleGoogleClick}
              onSubmit={handleSubmit}
              iabSource={detectInAppBrowser().source}
            />
          )}
        </div>

        {/* Trust strip + cross-link to the other mode so a returning
            teacher who landed on /signup (e.g. from an ad) can still
            get to /login, and vice versa. */}
        <p className="text-center text-xs text-stone-500 mt-6 leading-relaxed">
          Built for Canadian teachers · 3-day free trial, cancel anytime
          <br />
          {isSignup ? (
            <span className="text-stone-400">
              Already have an account?{" "}
              <Link to="/login" className="text-brand-700 hover:text-brand-800 font-medium">
                Sign in
              </Link>
            </span>
          ) : (
            <span className="text-stone-400">
              Don't have an account?{" "}
              <Link to="/signup" className="text-brand-700 hover:text-brand-800 font-medium">
                Sign up
              </Link>
            </span>
          )}
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Sub-components

function SignInForm(props: {
  mode: "signin" | "signup";
  email: string;
  setEmail: (v: string) => void;
  isLoading: boolean;
  error: string;
  onGoogle: () => void;
  onSubmit: (e: React.FormEvent) => void;
  /**
   * E41 v2: when the user is in an in-app browser (FB / IG / TikTok /
   * X etc), Google OAuth is silently broken (popup blocked, third-party
   * cookies stripped, redirect chain mangled). We saw this live on
   * 2026-05-24: an Instagram-IAB user clicked Google sign-in DESPITE
   * the IAB banner and never came back via the OAuth callback —
   * silently lost. Setting iabSource to a truthy string hides the
   * Google button entirely and routes them to email magic-link
   * (which works because the link opens in their real browser when
   * tapped from inside Gmail/Mail).
   */
  iabSource: string | null;
}) {
  const isSignup = props.mode === "signup";
  // Copy split: cold ad traffic landing on /signup needs to feel like
  // an account-creation page, not a returning-user login. Marketing
  // CTAs ("Try it free" / "Start free trial") now route here. The
  // returning-user /login page keeps its existing framing.
  const heading = isSignup ? "Create your account" : "Sign in";
  // No specific generation count in the subtitle — the free_credits_count
  // A/B (credits_1 / credits_2 / credits_3) means the actual allowance
  // varies per teacher. Stating a number we don't honor for everyone
  // would be a small breach of trust on the first screen they see.
  const subtitle = isSignup
    ? "Free to start. No credit card needed."
    : "Pick up where you left off.";
  const googleLabel = isSignup ? "Sign up with Google" : "Continue with Google";
  const submitLabel = props.isLoading
    ? "Sending…"
    : isSignup
      ? "Get my magic link"
      : "Send me a magic link";
  return (
    <>
      <h1 className="text-2xl font-bold text-stone-900 text-center mb-1">
        {heading}
      </h1>
      <p className="text-stone-500 text-center text-sm mb-7">
        {subtitle}
      </p>

      {props.error && (
        <div
          role="alert"
          aria-live="assertive"
          className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm"
        >
          {props.error}
        </div>
      )}

      {/* Google. Primary path — hidden inside in-app browsers (FB / IG
          / TikTok / etc) because Google OAuth silently fails there and
          we'd otherwise lose the teacher to a non-returning popup. The
          IAB banner above the card explains the situation. They can
          still sign up via the email magic-link below (which works
          because Gmail/Mail opens the link in their real browser). */}
      {!props.iabSource && (
        <>
          <button
            onClick={props.onGoogle}
            type="button"
            className="w-full py-3 bg-white border border-stone-200 hover:border-stone-300 hover:bg-stone-50 active:bg-stone-100 rounded-xl font-medium text-stone-700 flex items-center justify-center gap-3 mb-3 transition-all"
          >
            <GoogleGlyph />
            {googleLabel}
          </button>

          <div className="flex items-center gap-4 my-5">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-xs uppercase tracking-wider text-stone-400">
              or email
            </span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>
        </>
      )}

      {/* Email magic link */}
      <form onSubmit={props.onSubmit}>
        <label
          htmlFor="email"
          className="block text-sm font-medium text-stone-700 mb-2"
        >
          School email
        </label>
        <input
          id="email"
          type="email"
          value={props.email}
          onChange={(e) => props.setEmail(e.target.value)}
          placeholder="you@yourschool.ca"
          required
          autoComplete="email"
          className="w-full px-4 py-3 border border-stone-200 rounded-xl text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600 mb-3 transition-all"
        />
        <button
          type="submit"
          disabled={props.isLoading || !props.email}
          aria-busy={props.isLoading}
          className="w-full py-3 bg-brand-700 hover:bg-brand-800 disabled:bg-brand-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors shadow-sm"
        >
          {submitLabel}
        </button>
        {isSignup && (
          <p className="text-center text-xs text-stone-500 mt-3 leading-relaxed">
            We'll email you a one-click link to finish setup. No password to remember.
          </p>
        )}
      </form>
    </>
  );
}

function SentState(props: { email: string; onReset: () => void }) {
  // Look up the teacher's email provider so we can offer a one-click
  // "Open Gmail" button instead of making them tab-switch and hunt for
  // the message. Gmail gets a search-pre-filtered URL; other providers
  // open their generic inbox. School/work domains return null and we
  // fall back to the plain "Check your email" copy.
  const provider = getInboxProvider(props.email);

  const handleOpenInbox = () => {
    if (!provider) return;
    // Tracking so we can correlate "got magic-link, opened inbox" with
    // "completed signup" — measures whether this button actually helps.
    track("open_inbox_clicked", {
      provider: provider.name,
    });
    window.open(provider.inboxUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="text-center py-2">
      <div className="w-14 h-14 bg-emerald-50 ring-1 ring-emerald-200 rounded-full flex items-center justify-center mx-auto mb-5">
        <svg
          className="w-7 h-7 text-emerald-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h1 className="text-xl font-bold text-stone-900 mb-2">
        Check your email
      </h1>
      <p className="text-stone-600 mb-1">We sent a sign-in link to</p>
      <p className="font-medium text-stone-900 mb-5">{props.email}</p>

      {provider ? (
        <button
          type="button"
          onClick={handleOpenInbox}
          className="w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3 rounded-lg shadow-sm transition-colors mb-3 inline-flex items-center justify-center gap-2"
        >
          Open {provider.name}
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </button>
      ) : null}

      <p className="text-xs text-stone-500 mb-5 leading-relaxed">
        Didn't get it? Check spam, or wait a minute and try again.
      </p>
      <button
        type="button"
        onClick={props.onReset}
        className="text-brand-700 hover:text-brand-800 font-medium text-sm"
      >
        Use a different email
      </button>
    </div>
  );
}

/**
 * E41: in-app browser hint. Renders ONLY when we detect the user is in
 * Facebook / Instagram / TikTok / X / LinkedIn / Pinterest's in-app
 * browser. Those WebViews have history-leaking-cookie + popup-blocked +
 * third-party-script-blocked bugs that break Google OAuth and sometimes
 * magic-link click-throughs. The banner explains the "Open in Safari /
 * Chrome" escape hatch + fires a PostHog event so we can measure how
 * often this surface fires and whether teachers act on it.
 */
function InAppBrowserHint() {
  const [info] = useState(() => detectInAppBrowser());
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (info.isInAppBrowser) {
      track("in_app_browser_hint_shown", {
        source: info.source,
        platform: info.platform,
      });
    }
    // Only fire once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!info.isInAppBrowser || dismissed) return null;

  const openInstructions =
    info.platform === "ios"
      ? "Tap the ⋯ or ⤴ button below and choose \"Open in Safari\""
      : info.platform === "android"
        ? "Tap the ⋮ menu (top-right) and choose \"Open in Chrome\" or \"Open in browser\""
        : "Open this page in your phone's main browser";

  return (
    <div
      role="alert"
      className="mb-6 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-900 shadow-sm"
    >
      <p className="font-semibold mb-1">
        You're in the {info.source} app.
      </p>
      <p className="text-amber-900/90 leading-relaxed">
        Google sign-in and magic-link emails work best in a real browser.
        {" "}
        {openInstructions}, then come back to this page.
      </p>
      <button
        type="button"
        onClick={() => {
          track("in_app_browser_hint_dismissed", {
            source: info.source,
            platform: info.platform,
          });
          setDismissed(true);
        }}
        className="mt-2 text-xs font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2"
      >
        I'll continue here anyway
      </button>
    </div>
  );
}

/**
 * Map Stytch error codes to teacher-friendly copy. Falls back to a
 * generic message for unknown codes. The error codes themselves come
 * from `details.errorCode` on the ApiError payload — the backend's
 * `handleLoginOrSignup` surfaces Stytch's `error_type` (E31).
 *
 * Keeping this mapping in the FE means we can tune copy without a
 * backend deploy. If a code becomes common in PostHog we add a row;
 * unknown codes still fall through to the generic line.
 */
function toFriendlyAuthError(errorCode: unknown, isSignup: boolean): string {
  const code = typeof errorCode === "string" ? errorCode : "";
  switch (code) {
    case "invalid_email":
    case "email_invalid":
      return "That doesn't look like a valid email. Double-check and try again.";
    case "too_many_requests":
      return "Too many tries in a short window. Wait a minute and try again.";
    case "no_match_for_provided_magic_link_url":
      // This is an "our infrastructure is misconfigured" code that
      // shouldn't happen to teachers but is a known historical bug
      // (2026-05-24 URL allowlist regression). If it fires, route them
      // to Google sign-in instead of leaving them stranded.
      return "Something on our side is off — try the Google sign-in button below while we look into it.";
    case "user_lock_limit_reached":
      return "Too many sign-in attempts on this account. Wait a few minutes and try again.";
    case "stale_factors":
      return "This sign-in link is stale. Send a fresh one and use it within 30 minutes.";
    case "unable_to_auth_magic_link":
      return "This magic link is already used or expired. Send a fresh one.";
    default:
      return isSignup
        ? "We couldn't send your sign-up link. Try again, or use Google sign-in."
        : "We couldn't send your sign-in link. Try again, or use Google sign-in.";
  }
}

function GoogleGlyph() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}
